/**
 * MapZones.tsx — Mapa REAL y dinámico de "Mis zonas con leads" (Home) y del
 * mini-mapa de zona (Búsqueda). MapLibre GL JS vía react-map-gl/maplibre.
 *
 * - Tiles vector de CARTO (sin API key, con atribución): Positron en claro /
 *   Dark Matter en oscuro, conmutados por `useTheme`. Minimalista premium.
 * - Pins agregados por MUNICIPIO (centroide de sus leads) como GOTAS DE PUNTA
 *   FINA (el vértice inferior señala el centroide), color por banda de
 *   Opportunity Score (verde≥70 / ámbar / rojo) y tamaño por volumen; pulso
 *   animado solo en los de alta oportunidad.
 * - Auto-encuadre: `fitBounds` sobre las coordenadas reales (sin asumir región
 *   fija); cap de zoom para que un único municipio no quede pegado.
 * - Leads sin coords (lat/lng null) no se pintan.
 */
import { useEffect, useMemo, useRef } from 'react'
import Map, { Marker, type MapRef } from 'react-map-gl/maplibre'
import 'maplibre-gl/dist/maplibre-gl.css'
import type { InboxItem } from '@/services/listingsService'
import { computeZonePoints, pointsBounds } from '@/lib/homeAggregates'
import { useTheme } from '@/hooks/useTheme'

// CARTO basemaps (gratis, SIN API key, esquema OpenMapTiles). Positron (claro)
// / Dark Matter (oscuro). Las etiquetas se FUERZAN a español abajo (de fábrica
// vienen en inglés). Minimalista premium.
const STYLE_LIGHT = 'https://basemaps.cartocdn.com/gl/positron-gl-style/style.json'
const STYLE_DARK = 'https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json'

/** Expresión de etiqueta localizada a ES con fallbacks (esquema OpenMapTiles). */
const ES_LABEL = [
  'coalesce',
  ['get', 'name:es'],
  ['get', 'name:latin'],
  ['get', 'name'],
]

/**
 * Fuerza TODAS las capas de etiqueta del estilo a usar el topónimo en español
 * (los estilos de OpenFreeMap traen `name_en` por defecto → salían en inglés).
 * Se re-aplica en cada `style.load` (también al conmutar claro/oscuro).
 */
type RawMap = ReturnType<MapRef['getMap']>
function localizeLabelsToEs(map: RawMap): void {
  let layers
  try {
    layers = map.getStyle()?.layers
  } catch {
    return
  }
  if (!layers) return
  for (const layer of layers) {
    const tf = (layer as { layout?: Record<string, unknown> }).layout?.['text-field']
    if (tf === undefined) continue
    try {
      map.setLayoutProperty(layer.id, 'text-field', ES_LABEL)
    } catch {
      /* capa sin label editable: ignorar */
    }
  }
}

export default function MapZones({
  items,
  height = 196,
  showLegend = true,
}: {
  items: InboxItem[]
  height?: number
  showLegend?: boolean
}) {
  const { theme } = useTheme()
  const mapRef = useRef<MapRef | null>(null)

  const points = useMemo(() => computeZonePoints(items), [items])
  const bounds = useMemo(() => pointsBounds(points), [points])
  const maxCount = useMemo(
    () => Math.max(1, ...points.map((p) => p.count)),
    [points],
  )
  const boundsKey = bounds ? bounds.flat().map((n) => n.toFixed(3)).join(',') : ''

  const fit = () => {
    const map = mapRef.current
    if (!map || !bounds) return
    map.fitBounds(bounds, { padding: 34, maxZoom: 12, duration: 0 })
  }
  // Re-encuadra cuando cambian los puntos (cambio de zona/filtros/datos).
  useEffect(() => { fit() }, [boundsKey]) // eslint-disable-line react-hooks/exhaustive-deps

  // Al cargar (y en cada cambio de estilo, p.ej. tema) → etiquetas en español.
  const handleLoad = () => {
    const raw = mapRef.current?.getMap()
    if (!raw) return
    localizeLabelsToEs(raw)
    raw.on('style.load', () => {
      const m = mapRef.current?.getMap()
      if (m) localizeLabelsToEs(m)
    })
    fit()
  }

  if (points.length === 0) {
    return (
      <div className="mz-empty" style={{ height }}>
        Sin coordenadas que mostrar todavía.
      </div>
    )
  }

  // Centro inicial (fitBounds lo corrige en onLoad).
  const center = bounds
    ? { longitude: (bounds[0][0] + bounds[1][0]) / 2, latitude: (bounds[0][1] + bounds[1][1]) / 2 }
    : { longitude: -8.4, latitude: 43 }

  return (
    <div>
      <div className="mz-wrap" style={{ height }}>
        <Map
          ref={mapRef}
          initialViewState={{ longitude: center.longitude, latitude: center.latitude, zoom: 6 }}
          mapStyle={theme === 'dark' ? STYLE_DARK : STYLE_LIGHT}
          onLoad={handleLoad}
          attributionControl={{ compact: true }}
          dragRotate={false}
          touchZoomRotate={false}
          style={{ width: '100%', height: '100%' }}
        >
          {points.map((p) => {
            // Altura por volumen (rango contenido para que la punta siga fina);
            // ancho proporcional a la gota.
            const h = Math.round(26 + (p.count / maxCount) * 14)
            const w = Math.round(h * 0.72)
            return (
              <Marker key={p.city} longitude={p.lng} latitude={p.lat} anchor="bottom">
                <span
                  className={`mz-teardrop ${p.band}`}
                  style={{ width: w, height: h }}
                  title={`${p.city} · ${p.count} ${p.count === 1 ? 'lead' : 'leads'}`}
                >
                  <svg viewBox="0 0 24 32" width={w} height={h} aria-hidden="true">
                    <path d="M12 31.2C12 31.2 21.5 18.4 21.5 11A9.5 9.5 0 1 0 2.5 11C2.5 18.4 12 31.2 12 31.2Z" />
                    <circle className="hole" cx="12" cy="11" r="3.4" />
                  </svg>
                </span>
              </Marker>
            )
          })}
        </Map>
      </div>
      {showLegend && (
        <div className="map-legend" style={{ marginTop: 9 }}>
          <span className="lg high"><i />Alta oport.</span>
          <span className="lg mid"><i />Media</span>
          <span className="lg low"><i />Baja</span>
          <span style={{ marginLeft: 'auto' }}>Color = oportunidad · tamaño = nº de leads</span>
        </div>
      )}
    </div>
  )
}
